import { API_BASE } from "../config";
// =============================================================================
// File: frontend/src/components/PantryItemViewModal.jsx
// View full EDAMAM/product data for pantry items
// =============================================================================

import React, { useState, useEffect } from 'react';
import EdamamAttribution from './EdamamAttribution';

const PantryItemViewModal = ({ item, onClose, fetchWithAuth }) => {
  const [productData, setProductData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadProductData();
  }, [item]);

  const loadProductData = async () => {
    setLoading(true);
    setError('');

    try {
      // If item has product_cache_id, fetch from product_cache
      if (item.product_cache_id) {
        const response = await fetchWithAuth(`${API_BASE}/api/products/cache/${item.product_cache_id}`);
        if (response.ok) {
          const data = await response.json();
          setProductData(data);
        } else {
          setError('Product details not available');
        }
      } else if (item.barcode) {
        // Try to lookup by barcode
        const response = await fetchWithAuth(`${API_BASE}/api/products/lookup/upc`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ upc: item.barcode })
        });
        if (response.ok) {
          const data = await response.json();
          if (data.found) {
            setProductData(data.product);
          } else {
            setError('No product data available for this item');
          }
        }
      } else {
        setError('No barcode or product data linked to this item');
      }
    } catch (err) {
      console.error('Failed to load product data:', err);
      setError('Failed to load product details');
    } finally {
      setLoading(false);
    }
  };

  const getNutriScoreColor = (score) => {
    const colors = {
      'A': '#059669',
      'B': '#059669',
      'C': '#EAB308',
      'D': '#F97316',
      'E': '#EF4444'
    };
    return colors[score?.toUpperCase()] || '#9CA3AF';
  };

  const getNovaGroupLabel = (group) => {
    const labels = {
      1: 'Unprocessed',
      2: 'Processed Ingredients',
      3: 'Processed Foods',
      4: 'Ultra-Processed'
    };
    return labels[group] || 'Unknown';
  };

  const getNovaGroupColor = (group) => {
    const colors = {
      1: '#059669',
      2: '#059669',
      3: '#F97316',
      4: '#EF4444'
    };
    return colors[group] || '#9CA3AF';
  };

  const renderField = (label, value, formatter) => {
    if (value === null || value === undefined || value === '') return null;
    const displayValue = formatter ? formatter(value) : value;
    return (
      <div className="pivm-fieldRow">
        <span className="pivm-fieldLabel">{label}</span>
        <span className="pivm-fieldValue">{displayValue}</span>
      </div>
    );
  };

  const renderBadges = (items, badgeVariant) => {
    if (!items || items.length === 0) return <span className="pivm-noneText">None</span>;
    return (
      <div className="pivm-badgeList">
        {items.map((item, idx) => (
          <span key={idx} className={`pivm-badge ${badgeVariant}`}>
            {item.replace(/_/g, ' ').replace(/en:/g, '')}
          </span>
        ))}
      </div>
    );
  };

  return (
    <div className="pivm-overlay">
      <div className="pivm-modal">
        <div className="pivm-header">
          <h3 className="pivm-title">{'\u{1F4E6}'} {item.name}</h3>
          <button className="pivm-closeButton" onClick={onClose}>{'\u00D7'}</button>
        </div>

        <div className="pivm-content">
          {loading ? (
            <div className="pivm-noData">Loading product details...</div>
          ) : error && !productData ? (
            <>
              {/* Basic Item Info - always show */}
              <div className="pivm-section">
                <div className="pivm-sectionTitle">{'\u{1F4CB}'} Basic Information</div>
                {renderField('Item Name', item.name)}
                {renderField('Brand', item.brand)}
                {renderField('Category', item.category)}
                {renderField('Barcode/UPC', item.barcode)}
                {renderField('Quantity', `${item.quantity} ${item.unit}`)}
                {renderField('Scan Date', item.purchase_date, (d) => new Date(d).toLocaleDateString())}
                {renderField('Expiration Date', item.expiration_date, (d) => new Date(d).toLocaleDateString())}
                {renderField('Notes', item.notes)}
              </div>
              {/* Inventory Status - show even without product data */}
              {(item.storage_method || item.is_opened || item.is_frozen || item.expiry_source) && (
                <div className="pivm-section">
                  <div className="pivm-sectionTitle">{'\u{1F4E6}'} Inventory Status</div>
                  <div className="pivm-statusBadges">
                    {item.storage_method && (
                      <span className={`pivm-statusBadge pivm-statusBadge--${item.storage_method}`}>
                        {item.storage_method === 'refrigerator' ? 'Refrigerator' : item.storage_method === 'pantry' ? 'Pantry' : 'Freezer'}
                      </span>
                    )}
                    {item.is_opened && (
                      <span className="pivm-statusBadge pivm-statusBadge--opened">
                        Opened{item.opened_at ? ` ${new Date(item.opened_at).toLocaleDateString()}` : ''}
                      </span>
                    )}
                    {item.is_frozen && (
                      <span className="pivm-statusBadge pivm-statusBadge--frozen">
                        Frozen{item.frozen_at ? ` ${new Date(item.frozen_at).toLocaleDateString()}` : ''}
                      </span>
                    )}
                  </div>
                  {renderField('Expiry Source', item.expiry_source, (s) => {
                    const labels = { manual: 'Manual', estimated: 'Estimated', foodkeeper: 'USDA FoodKeeper', foodkeeper_opened: 'USDA FoodKeeper (after opening)', scanned: 'Scanned', api: 'API' };
                    return labels[s] || s;
                  })}
                </div>
              )}
              <div className="pivm-noDataSection">
                <p className="pivm-noDataWarning">{'\u26A0\uFE0F'} {error}</p>
                <p className="pivm-noDataHint">
                  This item may have been added manually without barcode lookup.
                </p>
              </div>
            </>
          ) : (
            <>
              {/* Basic Information */}
              <div className="pivm-section">
                <div className="pivm-sectionTitle">{'\u{1F4CB}'} Basic Information</div>
                {renderField('Product Name', productData?.label || item.name)}
                {renderField('Brand', productData?.brand || item.brand)}
                {renderField('Category', productData?.category || item.category)}
                {renderField('Barcode/UPC', productData?.upc || item.barcode)}
                {renderField('Data Source', productData?.source, (s) => s?.toUpperCase())}
                {renderField('Quantity in Pantry', `${item.quantity} ${item.unit}`)}
                {renderField('Scan Date', item.purchase_date, (d) => new Date(d).toLocaleDateString())}
                {renderField('Expiration Date', item.expiration_date, (d) => new Date(d).toLocaleDateString())}
              </div>

              {/* Inventory Status */}
              <div className="pivm-section">
                <div className="pivm-sectionTitle">{'\u{1F4E6}'} Inventory Status</div>
                <div className="pivm-statusBadges">
                  {item.storage_method && (
                    <span className={`pivm-statusBadge pivm-statusBadge--${item.storage_method}`}>
                      {item.storage_method === 'refrigerator' ? 'Refrigerator' : item.storage_method === 'pantry' ? 'Pantry' : 'Freezer'}
                    </span>
                  )}
                  {item.is_opened && (
                    <span className="pivm-statusBadge pivm-statusBadge--opened">
                      Opened{item.opened_at ? ` ${new Date(item.opened_at).toLocaleDateString()}` : ''}
                    </span>
                  )}
                  {item.is_frozen && (
                    <span className="pivm-statusBadge pivm-statusBadge--frozen">
                      Frozen{item.frozen_at ? ` ${new Date(item.frozen_at).toLocaleDateString()}` : ''}
                    </span>
                  )}
                  {item.out_of_stock && (
                    <span className="pivm-statusBadge pivm-statusBadge--outofstock">
                      Out of Stock
                    </span>
                  )}
                  {item.is_low_stock && !item.out_of_stock && (
                    <span className="pivm-statusBadge pivm-statusBadge--lowstock">
                      Low Stock
                    </span>
                  )}
                </div>
                {renderField('Expiry Source', item.expiry_source, (s) => {
                  const labels = { manual: 'Manual', estimated: 'Estimated', foodkeeper: 'USDA FoodKeeper', foodkeeper_opened: 'USDA FoodKeeper (after opening)', scanned: 'Scanned', api: 'API' };
                  return labels[s] || s;
                })}
                {item.foodkeeper_tips && (
                  <div className="pivm-foodkeeperTips">
                    <div className="pivm-foodkeeperLabel">USDA FoodKeeper Data</div>
                    <div className="pivm-foodkeeperContent">
                      {item.foodkeeper_tips.product_name && (
                        <div className="pivm-fieldRow">
                          <span className="pivm-fieldLabel">Matched Product</span>
                          <span className="pivm-fieldValue">{item.foodkeeper_tips.product_name}</span>
                        </div>
                      )}
                      {item.foodkeeper_tips.shelf_life && (
                        <div className="pivm-fieldRow">
                          <span className="pivm-fieldLabel">Shelf Life</span>
                          <span className="pivm-fieldValue">{item.foodkeeper_tips.shelf_life}</span>
                        </div>
                      )}
                      {item.foodkeeper_tips.after_opening && (
                        <div className="pivm-fieldRow">
                          <span className="pivm-fieldLabel">After Opening</span>
                          <span className="pivm-fieldValue">{item.foodkeeper_tips.after_opening}</span>
                        </div>
                      )}
                      {item.foodkeeper_tips.after_thawing && (
                        <div className="pivm-fieldRow">
                          <span className="pivm-fieldLabel">After Thawing</span>
                          <span className="pivm-fieldValue">{item.foodkeeper_tips.after_thawing}</span>
                        </div>
                      )}
                      {item.foodkeeper_tips.storage_tips && (
                        <p className="pivm-storageTip">{item.foodkeeper_tips.storage_tips}</p>
                      )}
                      {item.foodkeeper_tips.freeze_warning && (
                        <p className="pivm-freezeWarning">{item.foodkeeper_tips.freeze_warning}</p>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Nutrition Facts */}
              {(productData?.kcal_per_100g || productData?.protein_per_100g) && (
                <div className="pivm-section">
                  <div className="pivm-sectionTitle">{'\u{1F957}'} Nutrition (per 100g)</div>
                  <div className="pivm-nutritionGrid">
                    <div className="pivm-nutritionCard">
                      <div className="pivm-nutritionValue">
                        {Math.round(productData.kcal_per_100g || 0)}
                      </div>
                      <div className="pivm-nutritionLabel">Calories</div>
                    </div>
                    <div className="pivm-nutritionCard">
                      <div className="pivm-nutritionValue">
                        {(productData.protein_per_100g || 0).toFixed(1)}g
                      </div>
                      <div className="pivm-nutritionLabel">Protein</div>
                    </div>
                    <div className="pivm-nutritionCard">
                      <div className="pivm-nutritionValue">
                        {(productData.carbs_per_100g || 0).toFixed(1)}g
                      </div>
                      <div className="pivm-nutritionLabel">Carbs</div>
                    </div>
                    <div className="pivm-nutritionCard">
                      <div className="pivm-nutritionValue">
                        {(productData.fat_per_100g || 0).toFixed(1)}g
                      </div>
                      <div className="pivm-nutritionLabel">Fat</div>
                    </div>
                  </div>
                  {productData?.serving_size_g && (
                    <div className="pivm-servingInfo">
                      Serving size: {productData.serving_size_g}g
                      {productData.serving_description && ` (${productData.serving_description})`}
                    </div>
                  )}
                  <EdamamAttribution />
                </div>
              )}

              {/* Scores */}
              {(productData?.nutri_score || productData?.nova_group || productData?.eco_score) && (
                <div className="pivm-section">
                  <div className="pivm-sectionTitle">{'\u{1F4CA}'} Quality Scores</div>
                  <div className="pivm-scoresGrid">
                    {productData?.nutri_score && (
                      <div className="pivm-scoreItem">
                        <div className="pivm-scoreBadge" style={{ backgroundColor: getNutriScoreColor(productData.nutri_score) }}>
                          {productData.nutri_score.toUpperCase()}
                        </div>
                        <div className="pivm-scoreLabel">Nutri-Score</div>
                      </div>
                    )}
                    {productData?.nova_group && (
                      <div className="pivm-scoreItem">
                        <div className="pivm-scoreBadge" style={{ backgroundColor: getNovaGroupColor(productData.nova_group) }}>
                          {productData.nova_group}
                        </div>
                        <div className="pivm-scoreLabel">
                          NOVA ({getNovaGroupLabel(productData.nova_group)})
                        </div>
                      </div>
                    )}
                    {productData?.eco_score && (
                      <div className="pivm-scoreItem">
                        <div className="pivm-scoreBadge" style={{ backgroundColor: getNutriScoreColor(productData.eco_score) }}>
                          {productData.eco_score.toUpperCase()}
                        </div>
                        <div className="pivm-scoreLabel">Eco-Score</div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Health Labels */}
              {productData?.health_labels && productData.health_labels.length > 0 && (
                <div className="pivm-section">
                  <div className="pivm-sectionTitle">{'\u{1F3F7}\uFE0F'} Health Labels</div>
                  {renderBadges(productData.health_labels, 'pivm-badge--green')}
                </div>
              )}

              {/* Allergens */}
              {productData?.allergens_tags && productData.allergens_tags.length > 0 && (
                <div className="pivm-section">
                  <div className="pivm-sectionTitle">{'\u26A0\uFE0F'} Allergens</div>
                  {renderBadges(productData.allergens_tags, 'pivm-badge--red')}
                </div>
              )}

              {/* Additives */}
              {productData?.additives_tags && productData.additives_tags.length > 0 && (
                <div className="pivm-section">
                  <div className="pivm-sectionTitle">{'\u{1F9EA}'} Additives</div>
                  {renderBadges(productData.additives_tags, 'pivm-badge--yellow')}
                </div>
              )}

              {/* Ingredients */}
              {productData?.ingredients_text && (
                <div className="pivm-section">
                  <div className="pivm-sectionTitle">{'\u{1F4DD}'} Ingredients</div>
                  <p className="pivm-ingredientsText">
                    {productData.ingredients_text}
                  </p>
                </div>
              )}

              {/* Notes */}
              {item.notes && (
                <div className="pivm-section">
                  <div className="pivm-sectionTitle">{'\u{1F4CC}'} Your Notes</div>
                  <p className="pivm-notesText">
                    {item.notes}
                  </p>
                </div>
              )}

              {/* Product Image */}
              {productData?.image_url && (
                <div className="pivm-section">
                  <div className="pivm-sectionTitle">{'\u{1F4F7}'} Product Image</div>
                  <img
                    src={productData.image_url}
                    alt={productData.label}
                    className="pivm-productImage"
                  />
                </div>
              )}
            </>
          )}
        </div>

        <div className="pivm-footer">
          <button className="pivm-dismissButton" onClick={onClose}>
            Dismiss
          </button>
        </div>
      </div>
    </div>
  );
};

export default PantryItemViewModal;
